import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Check,
  ChevronDown,
  FileSpreadsheet,
  FileText,
  Loader2,
  MoreHorizontal,
  Paperclip,
  Plus,
  Send,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { AttachmentChip } from '@/components/attachment-chip';
import { AttachmentViewer, type ViewableFile } from '@/components/attachment-viewer';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Dialog } from '@/components/dialog';
import { DataList, DataListRow } from '@/components/data-list';
import { EmptyState } from '@/components/empty-state';
import { Input } from '@/components/input';
import { Select } from '@/components/select';
import { StatusPill } from '@/components/status-dot';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useAccounts,
  useCreateJournalEntry,
  useDeleteAttachment,
  useDeleteJournalEntry,
  useJournalEntries,
  usePostJournalEntry,
  useUploadAttachment,
  type JournalEntryInput,
  type JournalLineInput,
} from '@/lib/accounting';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import {
  currentPeriod,
  LedgerToolbar,
  PeriodFilter,
  periodRange,
  SearchBox,
  SortMenu,
  type Period,
  type SortOption,
} from '@/components/ledger-toolbar';
import { useDebouncedValue } from '@/lib/search';
import { dollars, formatBytes, formatDateFull } from '@/lib/format';
import { useFormError } from '@/lib/form-error';
import { cn } from '@/lib/cn';
import type { JournalEntryData } from '@/types/api';

const SORT_OPTIONS: SortOption[] = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'amount_desc', label: 'Amount: high → low' },
  { value: 'amount_asc', label: 'Amount: low → high' },
];

export function JournalTab() {
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  const [status, setStatus] = useState<'all' | 'draft' | 'posted'>(() => {
    const s = searchParams.get('status');
    return s === 'draft' || s === 'posted' ? s : 'all';
  });
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState<Period>(currentPeriod);
  const [sort, setSort] = useState('date_desc');
  const debouncedSearch = useDebouncedValue(search, 300);
  const range = periodRange(period);
  const { data: page, isLoading } = useJournalEntries({
    status: status === 'all' ? undefined : status,
    q: debouncedSearch || undefined,
    from: range.from,
    to: range.to,
    sort,
  });
  const [creating, setCreating] = useState(false);

  const list = page?.data ?? [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-1 rounded-md border border-[var(--color-border)] bg-card p-0.5">
          {(['all', 'draft', 'posted'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={cn(
                'rounded px-2.5 py-1 text-xs font-medium capitalize transition-colors',
                status === s
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {s}
            </button>
          ))}
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" /> New entry
        </Button>
      </div>

      <LedgerToolbar>
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search description or reference…"
        />
        <div className="flex items-center gap-2">
          <PeriodFilter value={period} onChange={setPeriod} />
          <SortMenu options={SORT_OPTIONS} value={sort} onChange={setSort} />
        </div>
      </LedgerToolbar>

      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : list.length === 0 ? (
        <EmptyState
          icon={FileSpreadsheet}
          title="No journal entries yet"
          description="Record your first entry — Stripe payments will start auto-posting once that hook lands."
          action={
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" /> New entry
            </Button>
          }
        />
      ) : (
        <DataList>
          {list.map((e) => (
            <JournalRow key={e.id} entry={e} focused={focusId === String(e.id)} />
          ))}
        </DataList>
      )}

      <JournalEntryDialog open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

function JournalRow({ entry, focused = false }: { entry: JournalEntryData; focused?: boolean }) {
  const post = usePostJournalEntry();
  const del = useDeleteJournalEntry();
  const confirm = useConfirm();
  const toast = useToast();
  const [expanded, setExpanded] = useState(focused);
  const [viewing, setViewing] = useState<ViewableFile | null>(null);
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focused) {
      rowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [focused]);

  // Posted entries are immutable (Post/Delete don't apply), so the actions
  // menu would be empty — only render it when there's actually something to do.
  const hasActions = !entry.is_posted;

  return (
    <div
      ref={rowRef}
      className={cn(
        focused &&
          'rounded-md ring-2 ring-[var(--color-primary)] ring-offset-2 ring-offset-[var(--color-surface)]',
      )}
    >
      <DataListRow onClick={() => setExpanded((v) => !v)}>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform',
            expanded ? 'rotate-0' : '-rotate-90',
          )}
        />
        <span className="w-32 shrink-0 text-xs text-[var(--color-foreground-subtle)]">
          {formatDateFull(entry.entry_date)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium text-foreground">{entry.description}</div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {entry.lines.length} {entry.lines.length === 1 ? 'line' : 'lines'}
            {sourceLabel(entry.source_type) ? ` · ${sourceLabel(entry.source_type)}` : ''}
          </div>
        </div>
        {entry.attachments.length === 1 ? (
          <AttachmentChip
            label={entry.attachments[0].file_name}
            title={entry.attachments[0].file_name}
            onClick={(e) => {
              e.stopPropagation();
              setViewing(entry.attachments[0]);
            }}
          />
        ) : entry.attachments.length > 1 ? (
          <AttachmentChip
            label={`${entry.attachments.length} files`}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(true);
            }}
          />
        ) : null}
        <span className="w-28 shrink-0 text-right tabular-nums">
          {dollars(entry.total_debit_cents)}
        </span>
        <StatusPill tone={entry.is_posted ? 'success' : 'warning'}>
          {entry.is_posted ? 'Posted' : 'Draft'}
        </StatusPill>
        {hasActions ? (
          <DropdownMenu>
            <DropdownMenuTrigger
              onClick={(e) => e.stopPropagation()}
              className="flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover/row:opacity-100 aria-expanded:opacity-100"
              aria-label="Entry actions"
            >
              <MoreHorizontal className="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem
                onClick={async () => {
                  try {
                    await post.mutateAsync(entry.id);
                    toast.success('Entry posted');
                  } catch (err) {
                    const msg =
                      (err as { response?: { data?: { message?: string } } })?.response?.data
                        ?.message ?? 'Could not post.';
                    toast.error('Cannot post', msg);
                  }
                }}
              >
                <Send className="size-3.5" /> Post
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Delete this draft?',
                    body: 'Posted entries can never be deleted. Drafts can be removed freely.',
                    confirmLabel: 'Delete',
                    tone: 'danger',
                  });
                  if (ok) {
                    await del.mutateAsync(entry.id);
                    toast.success('Draft deleted');
                  }
                }}
              >
                <Trash2 className="size-3.5" /> Delete draft
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          // Keep the last column aligned with rows that do show a menu.
          <span className="size-8 shrink-0" aria-hidden />
        )}
      </DataListRow>

      {expanded ? <JournalLines entry={entry} onView={setViewing} /> : null}

      <AttachmentViewer file={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

/**
 * Expanded per-entry detail — the double-entry breakdown. Each line shows the
 * account it hit and whether it was a debit or a credit, with the column
 * totals proving the entry balances.
 */
function JournalLines({
  entry,
  onView,
}: {
  entry: JournalEntryData;
  onView: (file: ViewableFile) => void;
}) {
  return (
    <div className="border-t border-border/50 bg-muted/20 px-5 py-3 pl-14">
      <div className="grid grid-cols-[1fr_7rem_7rem] gap-x-4 text-sm">
        <div className="pb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Account
        </div>
        <div className="pb-1.5 text-right text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Debit
        </div>
        <div className="pb-1.5 text-right text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          Credit
        </div>

        {entry.lines.map((line) => (
          <div key={line.id} className="col-span-3 grid grid-cols-subgrid border-t border-border/40 py-1.5">
            <div className="min-w-0">
              <span className="font-mono text-xs text-muted-foreground">{line.account_code}</span>{' '}
              <span className="text-foreground">{line.account_name}</span>
              {line.memo ? (
                <span className="ml-2 text-xs text-muted-foreground">— {line.memo}</span>
              ) : null}
            </div>
            <div className="text-right tabular-nums text-foreground">
              {line.debit_cents > 0 ? dollars(line.debit_cents) : ''}
            </div>
            <div className="text-right tabular-nums text-foreground">
              {line.credit_cents > 0 ? dollars(line.credit_cents) : ''}
            </div>
          </div>
        ))}

        <div className="col-span-3 grid grid-cols-subgrid border-t border-border/60 pt-1.5 text-xs font-medium">
          <div className="text-muted-foreground">Total</div>
          <div className="text-right tabular-nums">{dollars(entry.total_debit_cents)}</div>
          <div className="text-right tabular-nums">{dollars(entry.total_credit_cents)}</div>
        </div>
      </div>

      <JournalAttachments entry={entry} onView={onView} />

      {entry.reference ? (
        <p className="mt-3 border-t border-border/50 pt-3 text-[11px] text-muted-foreground">
          Reference <span className="font-mono">{entry.reference}</span>
        </p>
      ) : null}
    </div>
  );
}

/**
 * Supporting files for an entry — receipts, check photos, warranties, invoices.
 * Upload, view, remove. Stored on the JournalEntry so a single file
 * substantiates the ledger record regardless of whether it came from the bank
 * feed, Stripe, or a manual entry.
 */
function JournalAttachments({
  entry,
  onView,
}: {
  entry: JournalEntryData;
  onView: (file: ViewableFile) => void;
}) {
  const upload = useUploadAttachment();
  const remove = useDeleteAttachment();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // let the same file be re-picked after an error
    if (!file) return;
    try {
      await upload.mutateAsync({ entryId: entry.id, file });
      toast.success('File attached');
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not upload that file.';
      toast.error('Upload failed', msg);
    }
  }

  async function onDelete(mediaId: number) {
    try {
      await remove.mutateAsync({ entryId: entry.id, mediaId });
      toast.success('File removed');
    } catch {
      toast.error("Couldn't remove that file");
    }
  }

  return (
    <div className="mt-3 border-t border-border/50 pt-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          <Paperclip className="size-3" />
          Files
        </span>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={upload.isPending}
          className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:underline disabled:opacity-50"
        >
          {upload.isPending ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Upload className="size-3" />
          )}
          {upload.isPending ? 'Uploading…' : 'Attach file'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={onPick}
        />
      </div>

      {entry.attachments.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No files attached. Add a receipt, check photo, warranty, or invoice (PDF or image) to back
          this entry up.
        </p>
      ) : (
        <ul className="space-y-1">
          {entry.attachments.map((r) => (
            <li
              key={r.id}
              className="flex items-center gap-2 rounded-md border border-border/50 bg-card px-2.5 py-1.5"
            >
              {r.is_image ? (
                <button type="button" onClick={() => onView(r)} className="shrink-0">
                  <img src={r.url} alt={r.file_name} className="size-8 rounded object-cover" />
                </button>
              ) : (
                <FileText className="size-5 shrink-0 text-muted-foreground" />
              )}
              <button
                type="button"
                onClick={() => onView(r)}
                className="min-w-0 flex-1 truncate text-left text-sm text-foreground hover:underline"
              >
                {r.file_name}
              </button>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                {formatBytes(r.file_size)}
              </span>
              <button
                type="button"
                onClick={() => onDelete(r.id)}
                disabled={remove.isPending}
                aria-label={`Remove ${r.file_name}`}
                className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-destructive disabled:opacity-50"
              >
                <Trash2 className="size-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SOURCE_LABELS: Record<string, string> = {
  bank_transaction: 'Bank',
  approvable_payment: 'Stripe',
  subscription_bill: 'Subscription',
};

/** Human label for an entry's origin. Manual entries (and unknowns) get none. */
function sourceLabel(source: string | null): string | null {
  if (!source || source === 'manual') return null;
  return SOURCE_LABELS[source] ?? source;
}

type FormLine = {
  account_id: number | '';
  debit: string;
  credit: string;
  memo: string;
};

function emptyLine(): FormLine {
  return { account_id: '', debit: '', credit: '', memo: '' };
}

function JournalEntryDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data: accounts } = useAccounts(false);
  const create = useCreateJournalEntry();
  const { error, wrap } = useFormError();
  const today = new Date().toISOString().slice(0, 10);

  const [seeded, setSeeded] = useState(false);
  const [entryDate, setEntryDate] = useState(today);
  const [description, setDescription] = useState('');
  const [reference, setReference] = useState('');
  const [lines, setLines] = useState<FormLine[]>([emptyLine(), emptyLine()]);
  const [postOnSave, setPostOnSave] = useState(false);

  if (open && !seeded) {
    setEntryDate(today);
    setDescription('');
    setReference('');
    setLines([emptyLine(), emptyLine()]);
    setPostOnSave(false);
    setSeeded(true);
  }

  const totals = useMemo(() => {
    let debit = 0;
    let credit = 0;
    for (const l of lines) {
      debit += toCents(l.debit);
      credit += toCents(l.credit);
    }
    return { debit, credit, balanced: debit === credit && debit > 0 };
  }, [lines]);

  function updateLine(i: number, patch: Partial<FormLine>) {
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  }
  function addLine() {
    setLines((prev) => [...prev, emptyLine()]);
  }
  function removeLine(i: number) {
    setLines((prev) => (prev.length <= 2 ? prev : prev.filter((_, idx) => idx !== i)));
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const payload: JournalEntryInput = {
      entry_date: entryDate,
      description,
      reference: reference || null,
      post: postOnSave,
      lines: lines
        .filter((l) => l.account_id !== '')
        .map<JournalLineInput>((l) => ({
          account_id: Number(l.account_id),
          debit_cents: toCents(l.debit),
          credit_cents: toCents(l.credit),
          memo: l.memo || null,
        })),
    };
    const ok = await wrap(() => create.mutateAsync(payload));
    if (ok) {
      setSeeded(false);
      onClose();
    }
  }

  function handleClose() {
    setSeeded(false);
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} title="New journal entry" size="lg">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Date</span>
            <Input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} required />
          </label>
          <label className="col-span-2 block space-y-1 text-sm">
            <span className="font-medium text-[var(--color-foreground-muted)]">Description</span>
            <Input
              placeholder="Cash sale — Acme retainer June"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </label>
        </div>
        <label className="block space-y-1 text-sm">
          <span className="font-medium text-[var(--color-foreground-muted)]">Reference</span>
          <Input
            placeholder="INV-2026-0007"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
        </label>

        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wider text-[var(--color-foreground-subtle)]">
            Lines
          </div>
          <div className="overflow-hidden rounded-md border border-[var(--color-border)]">
            <div className="grid grid-cols-[1fr_120px_120px_1fr_32px] gap-2 border-b border-[var(--color-border)] bg-[var(--color-muted)] px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-foreground-subtle)]">
              <div>Account</div>
              <div className="text-right">Debit</div>
              <div className="text-right">Credit</div>
              <div>Memo</div>
              <div />
            </div>
            {lines.map((line, i) => (
              <div
                key={i}
                className="grid grid-cols-[1fr_120px_120px_1fr_32px] items-center gap-2 border-b border-[var(--color-border)] px-2 py-1.5 last:border-b-0"
              >
                <Select
                  value={line.account_id}
                  onChange={(e) =>
                    updateLine(i, {
                      account_id: e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                >
                  <option value="">Choose account…</option>
                  {(accounts ?? []).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} · {a.name}
                    </option>
                  ))}
                </Select>
                <Input
                  inputMode="decimal"
                  placeholder="0.00"
                  value={line.debit}
                  className="text-right tabular-nums"
                  onChange={(e) => updateLine(i, { debit: e.target.value, credit: '' })}
                />
                <Input
                  inputMode="decimal"
                  placeholder="0.00"
                  value={line.credit}
                  className="text-right tabular-nums"
                  onChange={(e) => updateLine(i, { credit: e.target.value, debit: '' })}
                />
                <Input
                  placeholder="optional"
                  value={line.memo}
                  onChange={(e) => updateLine(i, { memo: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => removeLine(i)}
                  disabled={lines.length <= 2}
                  aria-label="Remove line"
                  className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-destructive disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            ))}
            <div className="flex items-center justify-between gap-2 px-2 py-2">
              <button
                type="button"
                onClick={addLine}
                className="inline-flex items-center gap-1 text-xs font-medium text-[var(--color-primary)] hover:underline"
              >
                <Plus className="size-3" /> Add line
              </button>
              <div className="flex items-center gap-4 text-xs tabular-nums text-[var(--color-foreground-muted)]">
                <span>Debit {dollars(totals.debit)}</span>
                <span>Credit {dollars(totals.credit)}</span>
                {totals.balanced ? (
                  <Badge tone="success">
                    <Check className="mr-1 inline size-3" />
                    Balanced
                  </Badge>
                ) : (
                  <Badge tone={totals.debit === 0 ? 'neutral' : 'warning'}>
                    Diff {dollars(Math.abs(totals.debit - totals.credit))}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-[var(--color-foreground-muted)]">
          <input
            type="checkbox"
            checked={postOnSave}
            onChange={(e) => setPostOnSave(e.target.checked)}
            disabled={!totals.balanced}
          />
          Post immediately {totals.balanced ? '' : '(requires balanced entry)'}
        </label>

        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : postOnSave ? 'Save & post' : 'Save draft'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function toCents(input: string): number {
  if (input == null || input === '') return 0;
  const n = parseFloat(input);
  if (Number.isNaN(n) || n < 0) return 0;
  return Math.round(n * 100);
}

